import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SupportedDtoIn, { ContactClassificationDtoIn } from '@/models/in/SupportedDtoIn';
import { ContactClassificationStatus } from '@/models/ContactClassificationStatus';
import useFundContext from '@/contexts/Fund/useFundContext';
import { queryClient } from '@/hooks/QueryClient';
import { queryKeys } from '@/hooks/QueryKeys';
import Button from '@/components/ui/Button';
import CompletionUploadSection from '@/components/CompletionUploadSection/CompletionUploadSection';
import DocsTable from '../DocsTable/DocsTable';
import AddClassificationPopup from '@/pages/Supporteds/AddClassificationPopup/AddClassificationPopup';
import addIcon from '@/assets/icons/add.svg';

interface SupportedClassificationsInfoProps {
  supported: SupportedDtoIn;
  onRefreshList?: () => void;
}

const SupportedClassificationsInfo = ({ supported, onRefreshList }: SupportedClassificationsInfoProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const [isAddClassificationOpen, setIsAddClassificationOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between items-center">
        <h3 className="body-lr-semibold text-(--font-sub-title)">{t('supportedClassifications')}</h3>
        <Button onClick={() => setIsAddClassificationOpen(true)}>
          <img src={addIcon} alt="" className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-col max-w-[640px] gap-9 ">
        {supported.contactClassifications.map((contactClassification) => (
          <ClassificationWithCompletion
            key={`${supported.id}-${contactClassification.classification.name}`}
            contactClassification={contactClassification}
            fundId={currentFund?.id}
            onRefreshList={onRefreshList}
          />
        ))}
      </div>

      {currentFund && (
        <AddClassificationPopup
          fundId={currentFund.id}
          supportedId={supported.id}
          open={isAddClassificationOpen}
          setOpen={setIsAddClassificationOpen}
          onRefreshList={onRefreshList}
        />
      )}
    </div>
  );
};

interface ClassificationWithCompletionProps {
  contactClassification: ContactClassificationDtoIn;
  fundId?: number;
  onRefreshList?: () => void;
}

const ClassificationWithCompletion = ({ contactClassification, fundId, onRefreshList }: ClassificationWithCompletionProps) => {
  const { t } = useTranslation();
  const isPendingCompletion = contactClassification.status === ContactClassificationStatus.PendingCompletion;

  const completionMessage = useMemo(() => {
    if (!isPendingCompletion || !contactClassification.statusHistory) return null;
    const entry = [...contactClassification.statusHistory].reverse().find((e) => e.newStatus === ContactClassificationStatus.PendingCompletion);
    return entry?.officeComment?.comment || null;
  }, [isPendingCompletion, contactClassification.statusHistory]);

  const handleCompletionSuccess = () => {
    if (fundId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.supporteds.list(fundId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.supporteds.classifications(fundId) });
    }
    onRefreshList?.();
  };

  return (
    <div className="w-full flex flex-col gap-3">
      <p className="body-md-regular text-(--font-sub-title)">{t('classificationDetails')}</p>
      <DocsTable contactClassification={contactClassification} />
      {isPendingCompletion && fundId && (
        <CompletionUploadSection
          apiEndpoint={`funds/${fundId}/classifications/${contactClassification.id}/completion-documents`}
          onSuccess={handleCompletionSuccess}
          showAlert
          completionMessage={completionMessage}
        />
      )}
    </div>
  );
};

export default SupportedClassificationsInfo;
